import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, euro, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PaymentBadge } from "@/components/PaymentBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Printer, MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const { user, centers } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [centerFilter, setCenterFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const STATUS_FILTERS = [
    { value: "all", label: "Todas" },
    { value: "emitida", label: "Emitidas" },
    { value: "a_plazos", label: "A plazos" },
    { value: "pagada", label: "Pagadas" },
    { value: "anulada", label: "Anuladas" },
  ];
  const countBy = (v) => (v === "all" ? invoices.length : invoices.filter((i) => i.status === v).length);
  const filtered = statusFilter === "all" ? invoices : invoices.filter((i) => i.status === statusFilter);

  const load = async () => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    const [inv, pats] = await Promise.all([
      api.get("/invoices", { params }).then((r) => r.data),
      api.get("/patients", { params }).then((r) => r.data),
    ]);
    setInvoices(inv);
    setPatients(pats);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [centerFilter]);

  const patientName = (id) => {
    const p = patients.find((x) => x.id === id);
    return p ? `${p.name} ${p.surname}`.trim() : "—";
  };
  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";

  const openPdf = async (inv) => {
    try {
      const resp = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}/status?status=${status}`);
      toast.success("Estado actualizado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canManage = ["admin", "center_admin"].includes(user?.role) || user?.role === "gerente";
  const removeInvoice = async (inv) => {
    if (
      !window.confirm(
        `¿Eliminar definitivamente la factura ${inv.invoice_number}?\n\n• Los artículos volverán al stock\n• Se eliminarán sus cuotas de financiación\n• Si no es la última factura del año, quedará un hueco en la numeración`,
      )
    )
      return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Factura ${inv.invoice_number} eliminada`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Ingresos
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Facturas a pacientes
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">
            Para emitir una nueva factura, accede a la ficha del paciente.
          </p>
        </div>
        {["admin", "center_admin"].includes(user?.role) && (
          <div className="w-56">
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            data-testid={`invoice-filter-${f.value}`}
            onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              statusFilter === f.value
                ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                : "bg-white text-[#3F4744] border-[#E5E3DB] hover:border-[#4A5D4E]"
            }`}
          >
            {f.label} ({countBy(f.value)})
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead>Nº</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Paciente</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">IVA</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Estado</TableHead>
              {["admin", "center_admin"].includes(user?.role) && <TableHead>Forma de pago</TableHead>}
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={["admin", "center_admin"].includes(user?.role) ? 10 : 9} className="text-center text-[#6E7772] py-12">
                  <FileText strokeWidth={1.5} className="h-8 w-8 mx-auto mb-2 opacity-60" />
                  {statusFilter === "all" ? "Sin facturas emitidas." : "No hay facturas en este estado."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((inv) => (
              <TableRow key={inv.id} className="border-[#E5E3DB]" data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                <TableCell>{formatDate(inv.date)}</TableCell>
                <TableCell>
                  <Link to={`/pacientes/${inv.patient_id}`} className="text-[#4A5D4E] hover:underline">
                    {patientName(inv.patient_id)}
                  </Link>
                </TableCell>
                <TableCell>{centerName(inv.center_id)}</TableCell>
                <TableCell className="text-right">{euro(inv.subtotal)}</TableCell>
                <TableCell className="text-right">{euro(inv.iva_amount)}</TableCell>
                <TableCell className="text-right font-medium">{euro(inv.total)}</TableCell>
                <TableCell>
                  <Badge
                    className={
                      inv.status === "pagada"
                        ? "bg-[#D1E0D7] text-[#2A2F2C]"
                        : inv.status === "anulada"
                        ? "bg-[#E8D1D1] text-[#2A2F2C]"
                        : inv.status === "a_plazos"
                        ? "bg-[#F3E5D8] text-[#7C5A2A]"
                        : "bg-[#F3E5D8] text-[#2A2F2C]"
                    }
                  >
                    {inv.status === "a_plazos" ? "a plazos" : inv.status}
                  </Badge>
                </TableCell>
                {["admin", "center_admin"].includes(user?.role) && (
                  <TableCell data-testid={`invoice-payment-${inv.id}`}>
                    <PaymentBadge inv={inv} />
                  </TableCell>
                )}
                <TableCell className="space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openPdf(inv)}
                    data-testid={`pdf-${inv.id}`}
                  >
                    <Printer className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Link
                    to={`/pacientes/${inv.patient_id}`}
                    className="inline-block text-xs text-[#4A5D4E] hover:underline ml-2"
                    data-testid={`invoice-goto-patient-${inv.id}`}
                    title="Para cambiar el estado o editar la factura, accede a la ficha del paciente"
                  >
                    Ficha paciente
                  </Link>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeInvoice(inv)}
                      data-testid={`invoice-delete-${inv.id}`}
                      title="Eliminar factura definitivamente"
                    >
                      <Trash2 className="h-4 w-4 text-[#C07F67]" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
